import React, { useEffect, useState } from "react";
import { REPORT_REASONS, MAX_REPORT_NOTE } from "../helpers/reportRules";

// "Report this entry": a small dialog where a signed-in user tells the admins what's wrong with a wreck.
// `onSubmit({ reason, note })` returns a promise of { error? }; an error is shown here (the page toast sits behind this dialog).
export default function ReportDialog({ ship, onSubmit, onClose, onSuggestEdit, onOpenContact }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const needsNote = reason === "other";
  const ready = !!reason && !(needsNote && !note.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError("");
    const res = await onSubmit({ reason, note: note.trim() });
    // on success the parent closes this dialog; on failure we stay open and say why
    if (res?.error) { setError(res.error); setBusy(false); }
  };

  return (
    <div className="legal-backdrop" onClick={() => !busy && onClose()}>
      <article className="legal-modal report-modal" role="dialog" aria-label="Report this entry" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Report this entry</h2>
          <button type="button" className="slide-panel-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        <form onSubmit={submit}>
          <div className="legal-body">
            <p className="report-ship">
              <strong>{ship.title}</strong>
              <span>Boat ID: <code>{ship.id}</code></span>
            </p>
            <p>
              Tell the admins what's wrong and they'll take a look. Nothing changes until an admin reviews it, and you'll get a
              notification when they have.
            </p>
            <p className="panel-hint">
              Know the right answer (a wrong date, name or position)? You can fix it yourself with{" "}
              <button type="button" className="panel-link" onClick={onSuggestEdit}>✎ Suggest edit</button>, which usually helps more.
            </p>

            <label className="report-field">
              <span>What's the problem?</span>
              <select value={reason} onChange={(e) => setReason(e.target.value)} autoFocus>
                <option value="">Choose a reason…</option>
                {REPORT_REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>

            {reason === "photo" && (
              <p className="panel-hint">
                If you took the photo or hold its rights and want it removed or credited differently, you don't need an account: see the{" "}
                <button type="button" className="panel-link" onClick={onOpenContact}>Contact page</button>.
              </p>
            )}

            <label className="report-field">
              <span>{needsNote ? "Explain what's wrong (required)" : "More detail (optional)"}</span>
              <textarea
                rows={4}
                maxLength={MAX_REPORT_NOTE}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. The pin is on land; the wreck lies about 2 km offshore."
              />
              <small>{note.length}/{MAX_REPORT_NOTE}</small>
            </label>

            <p className="panel-hint">Reports are only seen by admins. Please don't include private personal information.</p>
            {error && <p className="panel-error" role="alert">{error}</p>}
          </div>

          <footer className="report-footer">
            <button type="button" className="panel-secondary report-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="panel-primary" disabled={!ready || busy}>{busy ? "Sending…" : "Send report"}</button>
          </footer>
        </form>
      </article>
    </div>
  );
}
